import { useState, useEffect, Fragment } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  runTransaction,
  serverTimestamp,
  onSnapshot,
  getDoc
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { toast } from 'react-toastify';
import { v4 } from 'uuid';
import { Search } from 'lucide-react';
import { Eye } from 'lucide-react';
import CancellationApprovalModal from '../../components/orders/CancellationApprovalModal';
import { calculateCommission } from '../../utils/commissionUtils';
import type { Order } from '../../types';
import { Dialog, Transition } from '@headlessui/react';
import { X, AlertTriangle, Package } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CancellationRequest {
  id: string;
  orderId: string;
  orderNumber: string;
  orderData?: Order;
  requestDate: string;
  status: string;
  reason: string;
  additionalNotes?: string;
  source: string;
  commission: number;
  customerName: string;
  sellerName: string;
  company?: {
    commercialName?: string;
    legalName?: string;
    vat?: string;
  };
}

export default function Cancellations() {
  const [cancellations, setCancellations] = useState<CancellationRequest[]>([]);
  const [filteredCancellations, setFilteredCancellations] = useState<CancellationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCancellation, setSelectedCancellation] = useState<CancellationRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [companies, setCompanies] = useState<{[key: string]: any}>({});
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.uid) return;

    const loadData = async () => {
      try {
        // 1. First fetch all companies
        const companiesRef = collection(db, 'companies');
        const companiesSnapshot = await getDocs(companiesRef);
        const companiesData: {[key: string]: any} = {};
        
        companiesSnapshot.forEach((doc) => {
          companiesData[doc.id] = doc.data();
        });
        
        setCompanies(companiesData);

        // 2. Then setup listener for cancellations
        const q = query(
          collection(db, 'cancellation_requests'),
          where('status', '==', selectedStatus)
        );

        const unsubscribe = onSnapshot(q, async (snapshot) => {
          const requests = await Promise.all(snapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data() as Partial<CancellationRequest>;
            
            let commission = 0;
            let buyerName = 'N/A';
            let sellerName = 'N/A';
            let company = {};

            // Usar dados do orderData que já está no documento
            if (data.orderData) {
              const orderData = data.orderData;

              // Get buyer name from orderData
              buyerName = orderData.shippingAddress?.name || orderData.customer?.name || 'N/A';

              // Se não tiver todos os dados do pedido, buscar do Firestore
              if (!orderData.shippingAddress?.address || !orderData.shippingMethod) {
                try {
                  const orderDoc = await getDoc(doc(db, 'orders', data.orderId));
                  if (orderDoc.exists()) {
                    const fullOrderData = orderDoc.data();
                    orderData.shippingAddress = {
                      ...orderData.shippingAddress,
                      ...fullOrderData.shippingAddress
                    };
                    orderData.shippingMethod = fullOrderData.shippingMethod;
                  }
                } catch (err) {
                  console.error('Erro ao carregar dados completos do pedido:', err);
                }
              }

              // Get seller info
              if (data.companyId) {
                const companyRef = doc(db, 'companies', data.companyId);
                const companyDoc = await getDoc(companyRef);
                if (companyDoc.exists()) {
                  const companyData = companyDoc.data();
                  company = {
                    commercialName: companyData.commercialName || companyData.name,
                    legalName: companyData.legalName || companyData.name,
                    vat: companyData.VAT || companyData.vat || companyData.taxNumber || companyData.taxId || companyData.nif || companyData.vatNumber || 'N/A'
                  };
                  sellerName = companyData.commercialName || companyData.name || 'N/A';
                }

                // Calculate commission
                const isManualOrder = !orderData.isOnlineSale && !orderData.isPrivateSale && orderData.source !== 'woocommerce';
                const commissionResult = await calculateCommission(
                  orderData.total || 0,
                  data.companyId,
                  isManualOrder
                );
                commission = commissionResult.totalCommission;
              }
            }

            return {
              ...data,
              id: docSnapshot.id,
              commission,
              company,
              customerName: buyerName,
              sellerName: sellerName
            } as CancellationRequest;
          }));
          
          setCancellations(requests);
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser, selectedStatus]);

  // Função para filtrar cancelamentos por status e termo de busca
  const filterCancellations = (status: string, term: string) => {
    let filtered = cancellations;

    // Filtrar por termo de busca
    if (term) {
      filtered = filtered.filter(cancellation => 
        cancellation.orderNumber.toLowerCase().includes(term.toLowerCase()) ||
        cancellation.orderData?.shippingAddress?.name?.toLowerCase().includes(term.toLowerCase()) ||
        cancellation.orderData?.shippingAddress?.email?.toLowerCase().includes(term.toLowerCase())
      );
    }

    setFilteredCancellations(filtered);
  };

  // Atualizar filtros quando mudar o status ou termo de busca
  useEffect(() => {
    filterCancellations(selectedStatus, searchTerm);
  }, [selectedStatus, searchTerm, cancellations]);

  const handleStatusChange = async (cancellationId: string, newStatus: string, orderId: string) => {
    try {
      // 1. Atualizar status da solicitação de cancelamento
      const cancellationRef = doc(db, 'cancellation_requests', cancellationId);
      await updateDoc(cancellationRef, {
        status: newStatus,
        reviewedBy: currentUser?.uid,
        reviewDate: new Date().toISOString()
      });

      // 2. Atualizar status da ordem
      if (orderId) {
        const orderRef = doc(db, 'orders', orderId);
        const orderUpdate: any = {
          lastUpdated: new Date().toISOString(),
          hasCancellationRequest: false // Sempre definir como false quando há uma decisão
        };

        // Só atualizar o status se for aprovado
        if (newStatus === 'approved') {
          orderUpdate.status = 'cancelled';
        }

        await updateDoc(orderRef, orderUpdate);
      }

      toast.success('Status atualizado com sucesso!');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const createWalletTransaction = async (transactionData: {
    amount: number;
    companyId: string;
    description: string;
    orderId: string;
    type: string;
  }) => {
    try {
      const transactionId = v4();
      
      // First update the wallet balance
      const walletRef = doc(db, 'wallets', transactionData.companyId);
      
      await runTransaction(db, async (transaction) => {
        const walletDoc = await transaction.get(walletRef);
        
        if (!walletDoc.exists()) {
          // If wallet doesn't exist, create it with initial balance
          transaction.set(walletRef, {
            balance: transactionData.amount,
            lastUpdated: serverTimestamp()
          });
        } else {
          // Update existing wallet balance
          const currentBalance = walletDoc.data().balance || 0;
          transaction.update(walletRef, {
            balance: currentBalance + transactionData.amount,
            lastUpdated: serverTimestamp()
          });
        }
        
        // Create the transaction record within the same transaction
        const transactionRef = doc(db, 'walletTransactions', transactionId);
        transaction.set(transactionRef, {
          ...transactionData,
          createdAt: serverTimestamp(),
          status: 'completed'
        });
      });

    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  };

  const handleApprovalConfirm = async (buyerRefund: number, sellerCharge: number) => {
    try {
      if (!selectedCancellation) {
        throw new Error('No cancellation selected');
      }

      const { orderData, orderId } = selectedCancellation;
      if (!orderData || !orderId) {
        throw new Error('Invalid cancellation data');
      }

      // Obter o ID da empresa, verificando todas as possíveis localizações
      let companyId = orderData.companyId;
      
      // Se não encontrar diretamente, tentar nos itens
      if (!companyId && orderData.items?.length > 0) {
        companyId = orderData.items[0].companyId;
      }
      
      // Se ainda não encontrar, tentar no sellerId (vendas privadas)
      if (!companyId) {
        companyId = orderData.sellerId;
      }

      if (!companyId) {
        console.error('Order data:', orderData);
        throw new Error('ID da empresa não encontrado. Verifique se é uma venda privada ou marketplace.');
      }

      console.log('Criando transação na carteira com:', {
        companyId,
        sellerCharge,
        orderId,
        orderData
      });

      // Create the transaction for the seller charge
      await createWalletTransaction({
        companyId: companyId,
        amount: -sellerCharge, // Negative because we're charging the seller
        type: 'cancellation_fee',
        description: `Taxa de cancelamento para pedido ${orderId}`,
        orderId: orderId
      });

      // If it's not a manual order and has a buyer refund, process it
      if (!orderData.isManual && buyerRefund > 0) {
        const buyerId = orderData.userId || orderData.buyerId;
        if (!buyerId) {
          throw new Error('Buyer ID not found');
        }

        await createWalletTransaction({
          companyId: buyerId,
          amount: buyerRefund,
          type: 'order_refund',
          description: `Reembolso do pedido ${orderId}`,
          orderId: orderId
        });
      }

      // Update the cancellation status
      await updateDoc(doc(db, 'cancellation_requests', selectedCancellation.id), {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: currentUser?.uid,
        buyerRefundAmount: buyerRefund,
        sellerChargeAmount: sellerCharge
      });

      // Update the order status
      await updateDoc(doc(db, 'orders', orderId), {
        status: 'cancelled',
        lastUpdated: serverTimestamp()
      });

      toast.success('Cancelamento aprovado com sucesso');
      setIsApprovalModalOpen(false);
      setSelectedCancellation(null);
    } catch (error) {
      console.error('Error approving cancellation:', error);
      toast.error('Erro ao aprovar cancelamento');
    }
  };

  const openApprovalModal = async (cancellation: CancellationRequest) => {
    try {
      console.log('Opening approval modal for cancellation:', cancellation);
      
      // For marketplace orders, ensure we have the wallet transaction
      if (cancellation.orderData?.source === 'marketplace') {
        if (!cancellation.walletTransaction) {
          console.log('Fetching wallet transaction for marketplace order:', cancellation.orderId);
          const transactionQuery = query(
            collection(db, 'walletTransactions'),
            where('orderId', '==', cancellation.orderId),
            where('type', '==', 'sale_payment')
          );
          const transactionSnapshot = await getDocs(transactionQuery);
          if (!transactionSnapshot.empty) {
            cancellation.walletTransaction = transactionSnapshot.docs[0].data();
            console.log('Found wallet transaction:', cancellation.walletTransaction);
          } else {
            console.log('No wallet transaction found for order:', cancellation.orderId);
          }
        } else {
          console.log('Using existing wallet transaction:', cancellation.walletTransaction);
        }
      }

      setSelectedCancellation(cancellation);
      setIsApprovalModalOpen(true);
    } catch (error) {
      console.error('Error loading wallet transaction:', error);
      toast.error('Erro ao carregar dados da transação');
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Gerenciamento de Cancelamentos</h1>
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-4 items-center justify-between">
        {/* Filtros de Status */}
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedStatus('pending')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              selectedStatus === 'pending'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Pendentes
          </button>
          <button
            onClick={() => setSelectedStatus('approved')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              selectedStatus === 'approved'
                ? 'bg-green-100 text-green-700'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Aprovados
          </button>
          <button
            onClick={() => setSelectedStatus('rejected')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              selectedStatus === 'rejected'
                ? 'bg-red-100 text-red-700'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Rejeitados
          </button>
        </div>

        {/* Campo de Busca */}
        <div className="w-full sm:w-96">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              placeholder="Pesquisar por nº do pedido ou dados do cliente..."
            />
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pedido
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vendedor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cliente
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data da Solicitação
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Comissão
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Motivo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCancellations.map((cancellation) => (
              <tr key={cancellation.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Link 
                    to={`/dashboard/orders/${cancellation.orderId}`}
                    className="text-sm font-medium text-blue-600 hover:text-blue-900"
                  >
                    {cancellation.orderNumber}
                  </Link>
                  <div className="text-sm text-gray-500">
                    {cancellation.source}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {cancellation.sellerName}
                  </div>
                  <div className="text-sm text-gray-500">
                    NIF: {cancellation.company?.vat || 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {cancellation.customerName}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {new Date(cancellation.requestDate).toLocaleString('pt-PT')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    €{cancellation.orderData?.total?.toFixed(2) || '0.00'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    €{cancellation.commission?.toFixed(2) || '0.00'}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">
                    {cancellation.reason}
                  </div>
                  {cancellation.additionalNotes && (
                    <div className="text-sm text-gray-500">
                      {cancellation.additionalNotes}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(cancellation.status)}`}>
                    {cancellation.status === 'pending' ? 'Pendente' :
                     cancellation.status === 'approved' ? 'Aprovado' :
                     cancellation.status === 'rejected' ? 'Rejeitado' : 
                     cancellation.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2 items-center justify-end">
                    <button
                      onClick={() => {
                        setSelectedCancellation(cancellation);
                        setIsModalOpen(true);
                      }}
                      className="text-gray-600 hover:text-gray-900"
                      title="Ver detalhes"
                    >
                      <Eye size={20} />
                    </button>
                    {cancellation.status === 'pending' && (
                      <>
                        <button
                          onClick={() => openApprovalModal(cancellation)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Aprovar
                        </button>
                        <button
                          onClick={() => handleStatusChange(cancellation.id, 'rejected', cancellation.orderId)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Rejeitar
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal de Visualização */}
      {selectedCancellation && (
        <Transition appear show={isModalOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setIsModalOpen(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
            </Transition.Child>

            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="mx-auto max-w-3xl rounded-lg bg-white p-6 shadow-xl">
                  <div className="flex justify-between items-center mb-4">
                    <Dialog.Title className="text-lg font-medium">
                      Detalhes do Pedido
                    </Dialog.Title>
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Alerta de Solicitação de Cancelamento */}
                  <div className="bg-yellow-50 p-4 rounded-md mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertTriangle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Solicitação de Cancelamento
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>Data da Solicitação: {selectedCancellation.requestDate}</p>
                          <p>Motivo: {selectedCancellation.reason}</p>
                          <p>Notas Adicionais: {selectedCancellation.additionalNotes || 'Nenhuma'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Informações do Cliente */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Informações do Cliente</h3>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Nome:</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedCancellation.orderData?.shippingAddress?.name || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Email:</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedCancellation.orderData?.shippingAddress?.email || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Telefone:</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedCancellation.orderData?.shippingAddress?.phone || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">NIF:</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedCancellation.orderData?.shippingAddress?.taxNumber || selectedCancellation.orderData?.shippingAddress?.nif || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Endereço de Entrega */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Endereço de Entrega</h3>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <p className="text-sm text-gray-900">
                        {selectedCancellation.orderData?.shippingAddress?.address || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-900">
                        {selectedCancellation.orderData?.shippingAddress?.postalCode} {selectedCancellation.orderData?.shippingAddress?.city}
                      </p>
                      <p className="text-sm text-gray-900">
                        {selectedCancellation.orderData?.shippingAddress?.district && 
                          `${selectedCancellation.orderData.shippingAddress.district}, `}
                        {selectedCancellation.orderData?.shippingAddress?.country || 'Portugal'}
                      </p>
                      {selectedCancellation.orderData?.shippingMethod && (
                        <p className="mt-2 text-sm text-gray-500">
                          Método de envio: {selectedCancellation.orderData.shippingMethod}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Items do Pedido */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Itens da Encomenda</h3>
                    <div className="bg-gray-50 rounded-md overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              IMAGEM
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              PRODUTO
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              QTD
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              PREÇO
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              TOTAL
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedCancellation.orderData?.items?.map((item, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="h-12 w-12 relative">
                                  {item.mainImage || item.image || item.imageUrl || item.productImage ? (
                                    <img 
                                      src={item.mainImage || item.image || item.imageUrl || item.productImage} 
                                      alt={item.name} 
                                      className="h-full w-full object-cover rounded-md"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        target.parentElement?.classList.add('sem-imagem');
                                      }}
                                    />
                                  ) : (
                                    <div className="h-full w-full rounded-md bg-gray-100 flex items-center justify-center text-gray-400 sem-imagem">
                                      <Package className="h-6 w-6" />
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.quantity}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                €{item.price?.toFixed(2)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                €{(item.price * item.quantity).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Totais */}
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Subtotal:</span>
                        <span className="text-gray-900">€{selectedCancellation.orderData?.subtotal?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Portes:</span>
                        <span className="text-gray-900">€{selectedCancellation.orderData?.shippingCost?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-gray-900">Total:</span>
                        <span className="text-gray-900">€{selectedCancellation.orderData?.total?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>
      )}
      <CancellationApprovalModal
        isOpen={isApprovalModalOpen}
        onClose={() => {
          setIsApprovalModalOpen(false);
          setSelectedCancellation(null);
        }}
        onConfirm={handleApprovalConfirm}
        cancellationRequest={selectedCancellation}
        walletTransaction={selectedCancellation?.walletTransaction}
      />
    </div>
  );
}
